import React, { useMemo } from "react";

// Simple, dependency-free password strength meter. Not cryptographically
// authoritative — just enough live signal so users see when a password is
// visibly weak while typing. Scored on length + character class diversity.
export function scorePassword(pw) {
  if (!pw) return { score: 0, label: "", tone: "", pct: 0 };
  let s = 0;
  if (pw.length >= 8) s += 1;
  if (pw.length >= 12) s += 1;
  const classes =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/[0-9]/.test(pw) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(pw) ? 1 : 0);
  if (classes >= 2) s += 1;
  if (classes >= 3) s += 1;
  if (classes >= 4 && pw.length >= 10) s += 1;
  // Downgrade obviously-weak sequences.
  if (/^(?:password|qwerty|12345|letmein|manodayam)/i.test(pw)) s = Math.min(s, 1);
  const bucket = Math.min(4, s);
  const meta = [
    { label: "Very weak", tone: "bg-destructive", pct: 15 },
    { label: "Weak",      tone: "bg-destructive/80", pct: 35 },
    { label: "Fair",      tone: "bg-[hsl(var(--chart-4))]", pct: 55 },
    { label: "Good",      tone: "bg-secondary", pct: 78 },
    { label: "Strong",    tone: "bg-primary", pct: 100 },
  ][bucket];
  return { score: bucket, ...meta };
}

export default function PasswordStrength({ value, className = "", testId = "password-strength" }) {
  const { score, label, tone, pct } = useMemo(() => scorePassword(value), [value]);
  if (!value) return null;
  return (
    <div className={"mt-1.5 " + className} data-testid={testId}>
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div className={"h-1.5 rounded-full transition-[width,background-color] duration-200 " + tone}
             style={{ width: `${pct}%` }} />
      </div>
      <div className="flex items-center justify-between mt-1 text-[11px] text-muted-foreground">
        <span data-testid={`${testId}-label`}>{label}</span>
        <span className="text-[10px]" data-testid={`${testId}-score`}>{score}/4</span>
      </div>
    </div>
  );
}
